import { useState, useEffect, useRef } from 'react';

interface CircularSliderProps {
  images: Array<{
    src: string;
    alt: string;
    id?: string | number;
  }>;
  imageSize?: string;
  gap?: string;
  animationDuration?: number;
  className?: string;
  responsive?: {
    mobile?: { imageSize?: string; gap?: string };
    tablet?: { imageSize?: string; gap?: string };
    desktop?: { imageSize?: string; gap?: string };
  };
}

const CircularSlider = ({ 
  images, 
  imageSize = '5rem', 
  gap = '1rem',
  animationDuration = 20,
  className = '',
  responsive
}: CircularSliderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldSlide, setShouldSlide] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentImageSize, setCurrentImageSize] = useState(imageSize);
  const [currentGap, setCurrentGap] = useState(gap);

  useEffect(() => {
    const getResponsiveValues = () => {
      if (!responsive) return { imageSize, gap };
      
      const width = window.innerWidth;
      if (width < 768 && responsive.mobile) {
        return {
          imageSize: responsive.mobile.imageSize || imageSize,
          gap: responsive.mobile.gap || gap
        };
      } else if (width < 1024 && responsive.tablet) {
        return {
          imageSize: responsive.tablet.imageSize || imageSize,
          gap: responsive.tablet.gap || gap
        };
      } else if (width >= 1024 && responsive.desktop) {
        return {
          imageSize: responsive.desktop.imageSize || imageSize,
          gap: responsive.desktop.gap || gap
        };
      }
      return { imageSize, gap };
    };

    const checkIfShouldSlide = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setContainerWidth(containerWidth);
        
        const responsiveValues = getResponsiveValues();
        setCurrentImageSize(responsiveValues.imageSize);
        setCurrentGap(responsiveValues.gap);
        
        // Calculate total width needed for all images
        const imageWidth = parseFloat(responsiveValues.imageSize);
        const gapWidth = parseFloat(responsiveValues.gap);
        const totalImagesWidth = images.length * imageWidth + (images.length - 1) * gapWidth;
        
        // Only slide if images exceed container width
        setShouldSlide(totalImagesWidth > containerWidth);
      }
    };

    checkIfShouldSlide();
    window.addEventListener('resize', checkIfShouldSlide);
    
    return () => window.removeEventListener('resize', checkIfShouldSlide);
  }, [images.length, imageSize, gap, responsive]);

  // Create enough duplicates for seamless infinite scroll
  const getDuplicatedImages = () => {
    if (!shouldSlide) return images;
    
    // Calculate how many duplicates we need
    const imageWidth = parseFloat(currentImageSize);
    const gapWidth = parseFloat(currentGap);
    const singleSetWidth = images.length * imageWidth + (images.length - 1) * gapWidth;
    const duplicatesNeeded = Math.ceil((containerWidth * 2) / singleSetWidth) + 1;
    
    const duplicatedImages = [];
    for (let i = 0; i < duplicatesNeeded; i++) {
      duplicatedImages.push(...images);
    }
    
    return duplicatedImages;
  };

  const duplicatedImages = getDuplicatedImages();

  return (
    <div 
      ref={containerRef}
      className={`overflow-hidden w-full ${className}`}
      style={{ overflow: 'hidden', width: '100%' }}
    >
      <div 
        className={`flex gap-4 py-4 ${shouldSlide ? 'animate-scroll' : ''}`}
        style={{
          display: 'flex',
          gap: currentGap,
          paddingTop: '1rem',
          paddingBottom: '1rem',
          animation: shouldSlide ? `scroll ${animationDuration}s linear infinite` : 'none',
          width: shouldSlide ? '200%' : 'auto',
          justifyContent: shouldSlide ? 'flex-start' : 'center'
        }}
      >
        {duplicatedImages.map((image, index) => (
          <img 
            key={`${image.id || index}-${Math.floor(index / images.length)}`}
            src={image.src}
            alt={image.alt}
            className="rounded-lg object-cover flex-shrink-0"
            style={{ 
              width: currentImageSize, 
              height: currentImageSize, 
              borderRadius: '0.5rem', 
              objectFit: 'cover', 
              flexShrink: 0 
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/assets/logo/logo2.png';
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CircularSlider;
